import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Cow, Eye, EyeSlash, ShieldCheck } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function ChangePassword() {
  const { user, setUser } = useAuth();
  const nav = useNavigate();
  const [current, setCurrent] = useState("");
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function submit(e) {
    e.preventDefault();
    setError("");
    if (pwd.length < 6) return setError("A nova senha precisa ter ao menos 6 caracteres.");
    if (pwd !== pwd2) return setError("As senhas não coincidem.");
    if (pwd === current) return setError("A nova senha deve ser diferente da atual.");
    setLoading(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: pwd });
      toast.success("Senha alterada com sucesso.");
      // clear the must_change flag in state so ProtectedRoute lets user through
      if (user) setUser({ ...user, must_change_password: false });
      nav("/dashboard", { replace: true });
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }

  const forced = !!user?.must_change_password;

  return (
    <div className="min-h-screen bg-[#F6F5F2] flex items-center justify-center p-6">
      <div className="w-full max-w-md bg-white border border-[#E2E0D8] rounded-sm p-6 md:p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-sm bg-[#2A5A3B] flex items-center justify-center">
            <ShieldCheck size={20} color="#fff" weight="duotone" />
          </div>
          <div>
            <div className="font-heading font-black text-xl leading-none tracking-tight">
              Trocar senha
            </div>
            <div className="text-[11px] uppercase tracking-[0.25em] text-[#4E544B] mt-1">
              {forced ? "Obrigatória no primeiro acesso" : "Manter conta segura"}
            </div>
          </div>
        </div>

        {forced && (
          <div className="mb-4 p-3 bg-[#FFF6E5] border border-[#D4A017]/30 rounded-sm text-xs text-[#8B5A00]">
            Por segurança, você precisa alterar a senha inicial fornecida pelo
            administrador antes de continuar.
          </div>
        )}

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#4E544B] block mb-2">
              Senha atual
            </label>
            <input
              data-testid="current-password"
              type={show ? "text" : "password"}
              required
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              className="input"
              autoComplete="current-password"
            />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#4E544B] block mb-2">
              Nova senha (mín. 6 caracteres)
            </label>
            <input
              data-testid="new-password"
              type={show ? "text" : "password"}
              required
              minLength={6}
              value={pwd}
              onChange={(e) => setPwd(e.target.value)}
              className="input"
              autoComplete="new-password"
            />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#4E544B] block mb-2">
              Confirmar nova senha
            </label>
            <input
              data-testid="new-password2"
              type={show ? "text" : "password"}
              required
              minLength={6}
              value={pwd2}
              onChange={(e) => setPwd2(e.target.value)}
              className="input"
              autoComplete="new-password"
            />
          </div>
          <label className="flex items-center gap-2 text-xs text-[#4E544B] select-none">
            <input
              type="checkbox"
              className="accent-[#2A5A3B] w-4 h-4"
              checked={show}
              onChange={(e) => setShow(e.target.checked)}
            />
            Mostrar senhas
          </label>

          {error && (
            <div
              data-testid="change-password-error"
              className="text-sm text-[#C05746] bg-[#FDEDED] border border-[#C05746]/20 rounded-sm px-3 py-2"
            >
              {error}
            </div>
          )}

          <div className="flex gap-3 pt-2">
            {!forced && (
              <button type="button" onClick={() => nav(-1)} className="btn-secondary">
                Cancelar
              </button>
            )}
            <button
              data-testid="submit-change-password"
              disabled={loading}
              type="submit"
              className="btn-primary flex-1"
            >
              {loading ? "Salvando…" : "Salvar nova senha"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
